import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { FiFileText } from 'react-icons/fi';
import myAxios from '../AxiosUrl';
import styles from './Policy.module.css';

const Policy = () => {
  const [selected, setSelected] = useState(0);
  const [policies, setPolicies] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');

  const policyTypes = [
    { id: 'privacy', label: 'Privacy Policy', content: 'privacy' },
    { id: 'terms', label: 'Terms & Conditions', content: 'terms' },
    { id: 'return', label: 'Return Policy', content: 'return' },
    { id: 'shipping', label: 'Shipping Policy', content: 'shipping' },
    { id: 'about', label: 'About Us', content: 'about' },
  ];

  const currentPolicy = policyTypes[selected];

  useEffect(() => {
    fetchPolicies();
  }, []);

  // Update editor when tab changes
  useEffect(() => {
    const policyData = policies[currentPolicy.id];
    if (policyData) {
      setTitle(policyData.title || '');
      setMetaTitle(policyData.metaTitle || '');
      setMetaDescription(policyData.metaDescription || '');

      // Convert HTML to Draft.js editor state
      if (policyData.content) {
        const contentBlock = htmlToDraft(policyData.content);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setTitle('');
      setMetaTitle('');
      setMetaDescription('');
      setEditorState(EditorState.createEmpty());
    }
  }, [selected, policies, currentPolicy.id]);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const uid = localStorage.getItem('id');

      const response = await myAxios.get(`policies/${uid}`);

      const policiesMap = {};
      response.data.forEach((policy) => {
        policiesMap[policy.pageType] = policy;
      });

      setPolicies(policiesMap);
    } catch (error) {
      console.error('Error fetching policies:', error);
      toast.error('Error loading policies');
    } finally {
      setLoading(false);
    }
  };

  const handleEditorChange = (state) => {
    setEditorState(state);
  };

  const uploadCallback = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve({ data: { link: reader.result } });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));

    if (!htmlContent || htmlContent.trim() === '<p></p>\n' || htmlContent.trim() === '') {
      toast.error('Please enter policy content');
      return;
    }

    try {
      setSaving(true);
      const uid = localStorage.getItem('id');

      await myAxios.post(
        `policy/${uid}`,
        {
          pageType: currentPolicy.id,
          title: title,
          content: htmlContent,
          metaTitle: metaTitle,
          metaDescription: metaDescription,
        }
      );

      toast.success(`${currentPolicy.label} saved successfully!`);
      fetchPolicies();
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Error saving policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${currentPolicy.label}?`)) {
      return;
    }

    try {
      setSaving(true);
      const uid = localStorage.getItem('id');

      await myAxios.delete(`policy/${uid}`, {
        data: { pageType: currentPolicy.id },
      });

      toast.success(`${currentPolicy.label} deleted successfully!`);

      // Remove from local state
      const updatedPolicies = { ...policies };
      delete updatedPolicies[currentPolicy.id];
      setPolicies(updatedPolicies);

      // Clear form
      setTitle('');
      setMetaTitle('');
      setMetaDescription('');
      setEditorState(EditorState.createEmpty());
    } catch (error) {
      console.error('Error deleting policy:', error);
      toast.error('Error deleting policy');
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = () => {
    const templates = {
      privacy: {
        title: 'Privacy Policy',
        content: `<h2>Privacy Policy</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>Information We Collect</h3>
<p>We collect information you provide directly to us when you create an account, make a purchase, or contact us. This may include:</p>
<ul>
  <li>Name and contact information</li>
  <li>Payment information</li>
  <li>Shipping address</li>
  <li>Purchase history</li>
</ul>

<h3>How We Use Your Information</h3>
<p>We use the information we collect to:</p>
<ul>
  <li>Process your orders and payments</li>
  <li>Communicate with you about your orders</li>
  <li>Send you marketing communications (with your consent)</li>
  <li>Improve our services and customer experience</li>
</ul>

<h3>Data Security</h3>
<p>We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

<h3>Your Rights</h3>
<p>You have the right to access, update, or delete your personal information at any time. Contact us if you wish to exercise these rights.</p>

<h3>Contact Us</h3>
<p>If you have questions about this Privacy Policy, please contact us at support@yourstore.com</p>`,
        metaTitle: 'Privacy Policy - Your Store Name',
        metaDescription: 'Read our privacy policy to understand how we collect, use, and protect your personal information.',
      },
      terms: {
        title: 'Terms & Conditions',
        content: `<h2>Terms & Conditions</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>Agreement to Terms</h3>
<p>By accessing and using our website, you agree to be bound by these Terms and Conditions and all applicable laws and regulations.</p>

<h3>Use License</h3>
<p>Permission is granted to temporarily download one copy of the materials on our website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title.</p>

<h3>Product Information</h3>
<p>We strive to provide accurate product descriptions and pricing. However, we do not warrant that product descriptions or other content is accurate, complete, reliable, current, or error-free.</p>

<h3>Pricing and Payment</h3>
<p>All prices are subject to change without notice. We reserve the right to refuse or cancel any order for any reason.</p>

<h3>Disclaimer</h3>
<p>The materials on our website are provided on an 'as is' basis. We make no warranties, expressed or implied, and hereby disclaim all other warranties.</p>

<h3>Limitations</h3>
<p>In no event shall our company be liable for any damages (including, without limitation, damages for loss of data or profit) arising out of the use or inability to use our materials.</p>`,
        metaTitle: 'Terms & Conditions - Your Store Name',
        metaDescription: 'Terms and conditions for using our website and purchasing our products.',
      },
      return: {
        title: 'Return Policy',
        content: `<h2>Return Policy</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>Return Window</h3>
<p>You have <strong>30 days</strong> from the date of delivery to return most items for a full refund.</p>

<h3>Eligible Items</h3>
<p>To be eligible for a return, items must be:</p>
<ul>
  <li>Unused and in the same condition as received</li>
  <li>In the original packaging</li>
  <li>Accompanied by proof of purchase</li>
</ul>

<h3>Non-Returnable Items</h3>
<p>The following items cannot be returned:</p>
<ul>
  <li>Perishable goods</li>
  <li>Intimate or sanitary goods</li>
  <li>Custom or personalized items</li>
  <li>Sale or clearance items</li>
</ul>

<h3>Return Process</h3>
<p>To initiate a return:</p>
<ol>
  <li>Contact our customer service team within the return window</li>
  <li>Receive your return authorization number</li>
  <li>Pack the item securely with all original packaging</li>
  <li>Ship the item using the provided return label</li>
</ol>

<h3>Refunds</h3>
<p>Once we receive and inspect your return, we'll process your refund within 5-7 business days. The refund will be issued to your original payment method.</p>

<h3>Exchanges</h3>
<p>If you need to exchange an item for a different size or color, please contact us to arrange the exchange.</p>`,
        metaTitle: 'Return Policy - Your Store Name',
        metaDescription: 'Learn about our hassle-free 30-day return policy and process.',
      },
      shipping: {
        title: 'Shipping Policy',
        content: `<h2>Shipping Policy</h2>
<p>Last updated: ${new Date().toLocaleDateString()}</p>

<h3>Where we deliver</h3>
<p>We deliver all over Bangladesh by courier.</p>

<h3>Delivery charge</h3>
<p>The delivery charge is shown at checkout before you confirm your order:</p>
<ul>
  <li><strong>Inside Dhaka:</strong> the inside-Dhaka charge</li>
  <li><strong>Outside Dhaka:</strong> the outside-Dhaka charge</li>
</ul>
<p>You pay the product price plus the delivery charge in cash when the parcel arrives (cash on delivery).</p>

<h3>Delivery time</h3>
<p>Inside Dhaka orders usually arrive within <strong>1-3 days</strong> and outside Dhaka within <strong>2-5 days</strong> after confirmation. Delivery can take longer to remote areas, during public holidays such as Eid, in bad weather or at very busy times.</p>

<h3>Order confirmation</h3>
<p>After you order, we may call or message you to confirm your order and address. Please keep your phone on so that we and the courier can reach you.</p>

<h3>If delivery fails</h3>
<p>If the courier cannot reach you or deliver the parcel, it comes back to us and we will contact you. If a parcel is refused without a valid reason, our Return Policy applies.</p>

<h3>Where is my order?</h3>
<p>Contact us with the name and mobile number you ordered with, and we will tell you where your order is.</p>`,
        metaTitle: 'Shipping Policy - Your Store Name',
        metaDescription: 'Delivery all over Bangladesh by courier: delivery charges inside and outside Dhaka, delivery time and cash on delivery.',
      },
      about: {
        title: 'About Us',
        content: `<h2>About Us</h2>
<p>Welcome to [Your Store Name]</p>

<h3>Our Story</h3>
<p>Founded in [Year], [Your Store Name] was born from a passion for [your industry/product category]. What started as a small venture has grown into a trusted destination for customers seeking quality products and exceptional service.</p>

<h3>Our Mission</h3>
<p>We are dedicated to providing our customers with:</p>
<ul>
  <li>High-quality products at competitive prices</li>
  <li>Outstanding customer service and support</li>
  <li>A seamless shopping experience</li>
  <li>Fast and reliable shipping</li>
</ul>

<h3>What Sets Us Apart</h3>
<p>At [Your Store Name], we believe in:</p>
<ul>
  <li><strong>Quality First:</strong> Every product is carefully selected and tested to meet our high standards</li>
  <li><strong>Customer Satisfaction:</strong> Your happiness is our priority, and we stand behind every purchase</li>
  <li><strong>Innovation:</strong> We continuously improve our offerings to bring you the latest and best products</li>
  <li><strong>Trust:</strong> Building long-term relationships with our customers through transparency and reliability</li>
</ul>

<h3>Our Team</h3>
<p>Our dedicated team works tirelessly to ensure you have the best shopping experience possible. From product selection to customer support, we're here to help every step of the way.</p>

<h3>Join Our Community</h3>
<p>We're more than just a store - we're a community of customers who share a love for quality products. Follow us on social media to stay updated on new arrivals, special offers, and more!</p>

<h3>Contact Us</h3>
<p>Have questions or feedback? We'd love to hear from you! Reach out through our contact page or connect with us on social media.</p>`,
        metaTitle: 'About Us - Your Store Name',
        metaDescription: 'Learn about our story, mission, and commitment to providing quality products and exceptional service.',
      },
    };

    const template = templates[currentPolicy.id];
    if (template) {
      setTitle(template.title);
      setMetaTitle(template.metaTitle);
      setMetaDescription(template.metaDescription);

      // Convert template HTML to editor state
      const contentBlock = htmlToDraft(template.content);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
      }

      toast.success('Template loaded successfully!');
    }
  };

  const currentPolicyData = policies[currentPolicy.id];
  const hasContent = currentPolicyData?.content;

  return (
    <div className={styles.container}>
      <ToastContainer />

      <div className={styles.header}>
        <h1>Store Policies</h1>
        <p>Manage your store's legal and operational policies</p>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {policyTypes.map((policy, index) => (
          <button
            key={policy.id}
            className={`${styles.tab} ${selected === index ? styles.tabActive : ''}`}
            onClick={() => setSelected(index)}
          >
            {policy.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {loading ? (
          <>
            {/* Content skeleton - form + preview */}
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
              {/* Form side */}
              <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
                {/* Title field */}
                <div>
                  <div className={styles.skeleton} style={{width: '80px', height: '14px', marginBottom: '8px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '40px', borderRadius: '8px'}}></div>
                </div>
                {/* Editor area */}
                <div>
                  <div className={styles.skeleton} style={{width: '60px', height: '14px', marginBottom: '8px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '40px', borderRadius: '8px', marginBottom: '4px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '300px', borderRadius: '8px'}}></div>
                </div>
                {/* SEO section */}
                <div style={{padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                  <div className={styles.skeleton} style={{width: '100px', height: '16px', marginBottom: '16px'}}></div>
                  <div className={styles.skeleton} style={{width: '80px', height: '14px', marginBottom: '8px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '40px', borderRadius: '8px', marginBottom: '16px'}}></div>
                  <div className={styles.skeleton} style={{width: '120px', height: '14px', marginBottom: '8px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '72px', borderRadius: '8px'}}></div>
                </div>
              </div>
              {/* Preview side */}
              <div>
                <div className={styles.skeleton} style={{width: '70px', height: '16px', marginBottom: '12px'}}></div>
                <div style={{padding: '20px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                  <div className={styles.skeleton} style={{width: '200px', height: '20px', marginBottom: '16px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '14px', marginBottom: '10px'}}></div>
                  <div className={styles.skeleton} style={{width: '90%', height: '14px', marginBottom: '10px'}}></div>
                  <div className={styles.skeleton} style={{width: '95%', height: '14px', marginBottom: '20px'}}></div>
                  <div className={styles.skeleton} style={{width: '160px', height: '18px', marginBottom: '12px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '14px', marginBottom: '10px'}}></div>
                  <div className={styles.skeleton} style={{width: '85%', height: '14px', marginBottom: '10px'}}></div>
                  <div className={styles.skeleton} style={{width: '92%', height: '14px', marginBottom: '20px'}}></div>
                  <div className={styles.skeleton} style={{width: '140px', height: '18px', marginBottom: '12px'}}></div>
                  <div className={styles.skeleton} style={{width: '100%', height: '14px', marginBottom: '10px'}}></div>
                  <div className={styles.skeleton} style={{width: '75%', height: '14px'}}></div>
                </div>
              </div>
            </div>
          </>
        ) : !hasContent ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}><FiFileText /></div>
            <h2>No {currentPolicy.label} yet</h2>
            <p>Create a {currentPolicy.label.toLowerCase()} to inform your customers about important terms.</p>
            <button className={styles.loadTemplateBtn} onClick={loadTemplate}>
              Load Template
            </button>
          </div>
        ) : (
          <>
            <div className={styles.formSection}>
              <div className={styles.field}>
                <label>Page Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Privacy Policy"
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label>Content</label>
                <div className={styles.editorWrapper}>
                  <Editor
                    editorState={editorState}
                    onEditorStateChange={handleEditorChange}
                    wrapperClassName={styles.editorWrapperClass}
                    editorClassName={styles.editorClass}
                    toolbar={{
                      options: [
                        'inline',
                        'blockType',
                        'fontSize',
                        'list',
                        'textAlign',
                        'link',
                        'image',
                        'history',
                      ],
                      inline: {
                        inDropdown: false,
                        options: ['bold', 'italic', 'underline', 'strikethrough'],
                      },
                      blockType: {
                        inDropdown: true,
                        options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
                      },
                      fontSize: {
                        options: [8, 10, 12, 14, 16, 18, 24, 30, 36, 48],
                      },
                      list: {
                        inDropdown: false,
                        options: ['unordered', 'ordered'],
                      },
                      textAlign: {
                        inDropdown: false,
                        options: ['left', 'center', 'right', 'justify'],
                      },
                      link: {
                        inDropdown: false,
                        options: ['link', 'unlink'],
                      },
                      image: {
                        uploadCallback: uploadCallback,
                        alt: { present: true, mandatory: false },
                        previewImage: true,
                      },
                    }}
                  />
                </div>
              </div>

              <div className={styles.seoSection}>
                <h3>SEO Settings</h3>
                <div className={styles.field}>
                  <label>Meta Title</label>
                  <input
                    type="text"
                    value={metaTitle}
                    onChange={(e) => setMetaTitle(e.target.value)}
                    placeholder="Meta title for search engines"
                    className={styles.input}
                  />
                </div>

                <div className={styles.field}>
                  <label>Meta Description</label>
                  <textarea
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    placeholder="Meta description for search engines"
                    className={styles.textarea}
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className={styles.previewSection}>
              <h3>Preview</h3>
              <div
                className={styles.preview}
                dangerouslySetInnerHTML={{
                  __html: draftToHtml(convertToRaw(editorState.getCurrentContent())) || '<p>No content to preview</p>',
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <button
          className={styles.loadTemplateBtn}
          onClick={loadTemplate}
          disabled={saving}
        >
          Load Template
        </button>
        <div className={styles.actionButtons}>
          {hasContent && (
            <button
              className={styles.deleteBtn}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Policy'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Policy;
